import React, { useEffect, useMemo, useState } from "react";
import { ScrollText, TrendingUp, TrendingDown, Scale, ShieldCheck, Loader2 } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const KIND = {
  entrada: { l: "Entrada", i: ShieldCheck, c: "bg-emerald-50 text-emerald-700" },
  saida: { l: "Saída", i: TrendingDown, c: "bg-rose-50 text-rose-600" },
  rendimento: { l: "Rendimento", i: TrendingUp, c: "bg-teal-50 text-teal-700" },
  ajuste: { l: "Ajuste", i: Scale, c: "bg-sky-50 text-sky-700" },
};
const FILTERS = [["", "Tudo"], ["entrada", "Entradas"], ["saida", "Saídas"], ["rendimento", "Rendimentos"], ["ajuste", "Ajustes"]];

const Row = ({ r, i }) => {
  const k = KIND[r.kind];
  return (
    <li className="py-2.5 flex items-start gap-3" data-testid={`extrato-row-${i}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${k.c}`}><k.i className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-slate-900 leading-tight truncate">{r.title}</p>
        <p className="text-[10.5px] text-slate-500 leading-tight truncate">{formatDateBR(r.date)} • {k.l}{r.subtitle ? ` • ${r.subtitle}` : ""}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-[13px] font-black tabular-nums ${r.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.amount >= 0 ? "+ " : "− "}{BRL(Math.abs(r.amount))}</p>
        <p className="text-[10.5px] text-slate-500 tabular-nums" data-testid={`extrato-bal-${i}`}>Saldo {BRL(r.balance)}</p>
      </div>
    </li>
  );
};

export const CaixaExtratoButton = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  useEffect(() => { if (open) { setData(null); api.get("/finance/caixa/extrato").then((r) => setData(r.data)).catch(() => setData({ items: [], balance: 0 })); } }, [open]);
  const items = useMemo(() => (data?.items || []).filter((r) => !kind || r.kind === kind), [data, kind]);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="w-full h-11 rounded-xl border-sky-200 text-sky-800 bg-white font-black" data-testid="btn-caixa-extrato">
        <ScrollText className="w-4 h-4 mr-2" /> Ver extrato do caixa
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] flex flex-col w-[calc(100vw-1.5rem)] sm:w-full [&>*]:min-w-0" data-testid="caixa-extrato-dialog">
          <DialogHeader className="text-left shrink-0">
            <DialogTitle className="font-heading font-black text-sky-800 flex items-center gap-2"><ScrollText className="w-5 h-5" /> Extrato do caixa</DialogTitle>
            {data && <p className="text-sm text-slate-600">Saldo atual: <b className="tabular-nums" data-testid="extrato-balance">{BRL(data.balance)}</b></p>}
          </DialogHeader>
          <div className="flex gap-1.5 overflow-x-auto shrink-0 pb-1">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`extrato-filter-${k || "all"}`}
                className={`h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${kind === k ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <div className="overflow-y-auto min-h-0 flex-1">
            {!data ? <div className="py-8 flex justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
              : items.length === 0 ? <p className="text-sm text-slate-500 italic py-6 text-center">Nenhum movimento.</p>
              : <ul className="divide-y divide-slate-100">{items.map((r, i) => <Row key={i} r={r} i={i} />)}</ul>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
